import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import moment from 'moment';
import { getOrdersListReport } from './shared/api/user';
import { useState, useEffect } from 'react';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import Typography from '@mui/material/Typography';


export default function OrderReport() {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [startDate, setStartDate] = useState(moment().subtract(30, 'days').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
  const [selectedCity, setSelectedCity] = useState('all');
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usdToPkr, setUsdToPkr] = useState(null);
  const init = async () => {
    setLoading(true);
    try {
      let rows = await getOrdersListReport(0);
      setOrders(rows.data.orders);
      
      // Extract unique cities from orders
      const uniqueCities = [...new Set(rows.data.orders.map(order => order.city || 'N/A'))];
      setCities(uniqueCities);
      
      filterOrders(rows.data.orders, startDate, endDate, 'all');
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    filterOrders(orders, startDate, endDate, selectedCity);
  }, [startDate, endDate, selectedCity, orders]);

    useEffect(() => {
    const fetchRate = async () => {
      try {
        const res = await fetch("https://open.er-api.com/v6/latest/USD");
        const data = await res.json();
        if (data?.result === "success") {
          setUsdToPkr(data.rates.PKR);
        }
      } catch (err) {
        console.error("Exchange rate fetch failed", err);
      }
    };
    fetchRate();
  }, []);

  const filterOrders = (ordersToFilter, start, end, city) => {
    if (!start || !end) return;
    
    const filtered = ordersToFilter.filter(order => {
      const orderDate = moment(order.createdAt);
      const dateInRange = orderDate.isBetween(moment(start).startOf('day'), moment(end).endOf('day'), null, '[]');
      const cityMatches = city === 'all' || order.city === city || (city === 'N/A' && !order.city);
      
      return dateInRange && cityMatches;
    });
    
    setFilteredOrders(filtered);
  };

  const handleCityChange = (event) => {
    setSelectedCity(event.target.value);
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      filteredOrders.map(order => ({
        'Order Date': moment(order.createdAt).format('DD/MM/YYYY'),
        'Order Time': moment(order.createdAt).format('hh:mm A'),
        'Customer ID': order.user?._id,        
        'Customer Name': `${order?.user?.firstName ? `${order?.user?.firstName} ${order?.user?.lastName || ''}` : order?.customerName || 'N/A'}`,
        'City': order?.city || "N/A",
        'Restaurant Name': order?.chef?.restaurantName || `${order?.chef?.firstName} ${order?.chef?.lastName}`,
        'Discounted (Y/N)':  (order?.nintyNineDiscount + order?.couponDiscount) > 0 ? "YES" : "NO",
        // 'Trip Status': order.orderType === 'PICKUP' ? 'No Trip' : 'N/A',
        'Order Completed (Y/N)': order?.orderStatus === "COMPLETED" ? "YES" : "NO",
        'Delivery Date': order?.orderStatus === "COMPLETED" ?  moment(order.updatedAt).format('DD/MMM/yyyy')  : null,
        'Delivery Time': order?.orderStatus === "COMPLETED" ?  moment(order.updatedAt).format('h:mm A')  : null,
        'Duration (Min)': order?.orderStatus === 'COMPLETED' && order.updatedAt
                      ? moment(order.updatedAt).diff(order.createdAt, 'minutes')   // integer minutes
                      : '—',
        // 'Order Type': order?.orderType,
        // 'Payment Status': order?.paymentStatus,
        // 'Order Value': order.serviceFee + order.total,
        'Order Value': order.orderFrom === "US" && usdToPkr
          ? (order.serviceFee + order.total) * usdToPkr
          : (order.serviceFee + order.total),
        'Order From': order.orderFrom,
        'Cancelled By': order.cancelledBy,
        'Customer Contact': order?.user?.phoneNumber,
         'Cancellation Reason': order.cancellationReason,
        
        // 'Restaurant Contact': order?.chef?.phoneNumber,
        // 'Order Placed': order?.createdAt,
        
        
      }))
    );
    
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(data, `orders_report_${moment().format('YYYYMMDD_HHmmss')}.xlsx`);
  };

const exportToExcelVendor = () => {
  // ✅ Filter completed orders first
  const completedOrders = filteredOrders.filter(order => order.orderStatus === "COMPLETED");

  if (completedOrders.length === 0) {
    alert("No completed orders found to export.");
    return;
  }

  const worksheet = XLSX.utils.json_to_sheet(
    completedOrders.map(order => ({
      'Order Date': moment(order.createdAt).format('DD/MM/YYYY'),
      'Order Time': moment(order.createdAt).format('hh:mm A'),
      'Customer ID': order.user?._id,        
      'Customer Name': `${order?.user?.firstName} ${order?.user?.lastName || ''}`,
      'City': order?.city || "N/A",
      'Restaurant Name': order?.chef?.restaurantName || `${order?.chef?.firstName} ${order?.chef?.lastName}`,
      'Discounted (Y/N)': (order?.nintyNineDiscount + order?.couponDiscount) > 0 ? "YES" : "NO",
      'Order Completed (Y/N)': "YES", // all are completed anyway
      'Delivery Date': moment(order.updatedAt).format('DD/MMM/yyyy'),
      'Delivery Time': moment(order.updatedAt).format('h:mm A'),
      'Duration (Min)': moment(order.updatedAt).diff(order.createdAt, 'minutes'),
      'Order Value': order.orderFrom === "US" && usdToPkr
        ? (order.serviceFee + order.total) * usdToPkr
        : (order.serviceFee + order.total),
      // 'Order From': order.orderFrom,
      // 'Cancelled By': order.cancelledBy,
    }))
  );

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Completed Orders");

  // ✅ Download file
  XLSX.writeFile(workbook, `CompletedOrders_${moment().format('YYYYMMDD_HHmm')}.xlsx`);
};

  return (
    <Box sx={{ p: 0 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 1 }}>
        Daily Order Report
      </Typography>
      <Box sx={{ 
        mb: 3, 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', md: 'center' },
        gap: 2
      }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          flexGrow: 1
        }}>
          <TextField
            label="From Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            fullWidth
          />
          <TextField
            label="To Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel id="city-filter-label">City</InputLabel>
            <Select
              labelId="city-filter-label"
              value={selectedCity}
              label="City"
              onChange={handleCityChange}
            >
              <MenuItem value="all">All Cities</MenuItem>
              {cities.map((city) => (
                <MenuItem key={city} value={city}>{city}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={exportToExcel}
          disabled={loading || filteredOrders.length === 0}
          sx={{ 
            mt: { xs: 2, md: 0 },
            height: '56px' // Match the height of other filter controls
          }}
        >
          Export to Excel
        </Button>
{ false && (
         <Button 
          variant="contained" 
          color="primary" 
          onClick={exportToExcelVendor}
          disabled={loading || filteredOrders.length === 0}
          sx={{ 
            mt: { xs: 2, md: 0 },
            height: '56px' // Match the height of other filter controls
          }}
        >
          Vendor Deliveries
        </Button> )}
      </Box>

      

      <TableContainer component={Paper}  sx={{
    width: '100%',            
    maxWidth: '100%',         
    overflowX: 'auto',
    maxHeight: 'calc(100vh - 220px)',
  }}>
        <Table stickyHeader sx={{
      minWidth: 1500,          
      width: 'max-content',          
    }} aria-label="report table">
          <TableHead>
            <TableRow>
              <TableCell align="right">Order Date</TableCell>
              <TableCell align="right">Order Time</TableCell>              
              <TableCell align="left">Customer Name</TableCell>
              {/* <TableCell align="left">Customer Address</TableCell> */}
              <TableCell align="left">City</TableCell>
              <TableCell>Restaurant Name</TableCell>
              {/* <TableCell align="center">Trip Status</TableCell> */}
              <TableCell align="center">Discounted (Y/N)</TableCell>    
              {/* <TableCell align="center">Order Placed</TableCell>     */}
              <TableCell align="center">Order Completed (Y/N)</TableCell>              
               <TableCell align="left">Delivery Date</TableCell>              
               <TableCell align="left">Delivery Time</TableCell>                            
               <TableCell align="center">Duration (Min)</TableCell>                            
              {/* <TableCell align="center">Order Type</TableCell> */}
              {/* <TableCell align="center">Payment Status</TableCell> */}
              <TableCell align="center">Order Value</TableCell>
              <TableCell align="center">Order From</TableCell>
              <TableCell align="right">Onboard Status</TableCell>
              <TableCell align="right">Cancelled By</TableCell>
              <TableCell align="right">Cancellation Reason</TableCell>
              {/* <TableCell align="center">Contact Restaurant</TableCell>               */}
              
              
              
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center">Loading...</TableCell>
              </TableRow>
            ) : filteredOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  No orders found for the selected filters
                </TableCell>
              </TableRow>
            ) : (
              filteredOrders.map((row) => (
                
                <TableRow key={row._id} hover>
                  <TableCell align="right">{moment(row.createdAt).format('DD/MM/YYYY')}</TableCell>
                  <TableCell align="right">{moment(row.createdAt).format('hh:mm A')}</TableCell>
                 
                  
                  <TableCell align="left">{row?.user?.firstName ? `${row?.user?.firstName} ${row?.user?.lastName || ''}` : row?.customerName || 'N/A'}</TableCell>
                  
                  {/* <TableCell align="left">{row?.address }</TableCell> */}
                  <TableCell align="left">{row?.city || "N/A"}</TableCell>
                   {row?.chef?.restaurantName ? (
                    <TableCell component="th" scope="row">
                      {row?.chef?.restaurantName}
                    </TableCell>
                  ) : (
                    <TableCell component="th" scope="row">
                      {row?.chef?.firstName} {row?.chef?.lastName}
                    </TableCell>
                  )}
                  {/* <TableCell align="center">{row.orderType === 'PICKUP' ? 'No Trip' : 'N/A'}</TableCell> */}
                   <TableCell align="center">
                      {(row?.nintyNineDiscount + row?.couponDiscount) > 0 ? "YES" : "NO"}
                  </TableCell>  
                  {/* <TableCell align="left">{moment(row.createdAt).format('DD/MM/yyyy, h:mm a')}</TableCell>                   */}
                   <TableCell align="center">
                      {row?.orderStatus === "COMPLETED" ? "YES" : "NO"}
                  </TableCell>
                   <TableCell align="left">
                    {row?.orderStatus === "COMPLETED" ?  moment(row.updatedAt).format('DD/MMM/yyyy')  : null}
                  </TableCell>
                  <TableCell align="left">
                    {row?.orderStatus === "COMPLETED" ?  moment(row.updatedAt).format('h:mm A')  : null}
                  </TableCell>
                 <TableCell align="center">
                    {row?.orderStatus === 'COMPLETED' && row.updatedAt
                      ? moment(row.updatedAt).diff(row.createdAt, 'minutes')   // integer minutes
                      : '—'}
                  </TableCell>
                  {/* <TableCell align="center">{row?.orderType}</TableCell> */}
                  {/* <TableCell align="center">{row?.paymentStatus}</TableCell> */}
                  {/* <TableCell align="center">{`${(row.serviceFee + row.total).toFixed(2)}`}</TableCell> */}
                   <TableCell onClick={() => details(row._id)} align="right">
                        {row?.orderFrom === "US" && usdToPkr
                          ? `Rs. ${((row.serviceFee + row.total) * usdToPkr).toFixed(2)}`
                          : `Rs. ${(row.serviceFee + row.total).toFixed(2)}`}
                    </TableCell>
                    { <TableCell align="center">{`${(row.orderFrom)}`}</TableCell> }
                                  <TableCell onClick={() => details(row._id)} align="center">
                                {row?.receiver?.isOnboarded ? "Onboard" : ""}
                              </TableCell>
                    { <TableCell align="center">{`${(row.cancelledBy)}`}</TableCell> }
                                  <TableCell onClick={() => details(row._id)} align="center">
                                {row?.receiver?.cancelledBy  }
                              </TableCell>
                  {/* <TableCell align="center">{row?.chef?.phoneNumber}</TableCell>                   */}
                  <TableCell align="right">{row.cancellationReason}</TableCell>
                  
                  
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}